import type { AnimatorResult } from '../systems/TravelTypes';

/**
 * Evaluates Catmull-Rom spline position, tangent, and curvature.
 * Returns updated smoothTurn value. Zero allocations.
 */
export function evalCatmullRom(
  t: number,
  p0x: number, p0z: number, p1x: number, p1z: number,
  p2x: number, p2z: number, p3x: number, p3z: number,
  r: AnimatorResult, smoothTurn: number,
): number {
  const t2 = t * t, t3 = t2 * t;
  r.worldX = 0.5 * ((2 * p1x)
    + (-p0x + p2x) * t
    + (2 * p0x - 5 * p1x + 4 * p2x - p3x) * t2
    + (-p0x + 3 * p1x - 3 * p2x + p3x) * t3);
  r.worldZ = 0.5 * ((2 * p1z)
    + (-p0z + p2z) * t
    + (2 * p0z - 5 * p1z + 4 * p2z - p3z) * t2
    + (-p0z + 3 * p1z - 3 * p2z + p3z) * t3);
  const dx = 0.5 * ((-p0x + p2x)
    + (4 * p0x - 10 * p1x + 8 * p2x - 2 * p3x) * t
    + (-3 * p0x + 9 * p1x - 9 * p2x + 3 * p3x) * t2);
  const dz = 0.5 * ((-p0z + p2z)
    + (4 * p0z - 10 * p1z + 8 * p2z - 2 * p3z) * t
    + (-3 * p0z + 9 * p1z - 9 * p2z + 3 * p3z) * t2);
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len > 1e-6) {
    r.dirX = dx / len; r.dirZ = dz / len;
    const ddx = (2 * p0x - 5 * p1x + 4 * p2x - p3x)
      + (-3 * p0x + 9 * p1x - 9 * p2x + 3 * p3x) * t;
    const ddz = (2 * p0z - 5 * p1z + 4 * p2z - p3z)
      + (-3 * p0z + 9 * p1z - 9 * p2z + 3 * p3z) * t;
    const raw = (dx * ddz - dz * ddx) / (len * len * len);
    const clamped = Math.max(-2, Math.min(2, raw));
    smoothTurn += (clamped - smoothTurn) * 0.06;
    r.turnRate = smoothTurn;
  }
  return smoothTurn;
}
